import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import type { ParserEventLogEntry, ParserEventLogQueryFilters, ParserInfo } from '@mariana/labos-client';
import { limsClient } from '../api/limsClient';
import { useLab } from '../LabContext';
import { useAuth } from '../auth/AuthContext';
import { formatDate } from '../utils/formatDate';
import { FileViewer } from './FileViewer';
import { Breadcrumb } from './Breadcrumb';
import { PaginationControls } from './PaginationControls';
import { useURLSyncedFilters } from '../hooks/useURLSyncedFilters';
import { ParserLogIcon } from './icons';
import './ParserLogsView.css';

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'success', label: 'Success' },
  { value: 'partial', label: 'Partial' },
  { value: 'error', label: 'Error' },
];

const ParserLogsView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { eventLabId } = useLab();
  const auth = useAuth();

  const [events, setEvents] = useState<ParserEventLogEntry[]>([]);
  const [parsers, setParsers] = useState<ParserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [count, setCount] = useState(0);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [fileViewer, setFileViewer] = useState<{ fileName: string; content: string } | null>(null);
  const [fileLoading, setFileLoading] = useState<number | null>(null);
  const [rerunLoading, setRerunLoading] = useState<number | null>(null);
  const [liveTestStates, setLiveTestStates] = useState<Record<number, Record<number, string>>>({});
  const [testStatesLoading, setTestStatesLoading] = useState<number | null>(null);

  useEffect(() => {
    limsClient.listParsers().then(setParsers).catch(() => setParsers([]));
  }, []);

  const {
    filters,
    handleFilterChange,
    handleClearFilters,
    handlePageChange,
    handleLimitChange,
    hasActiveFilters,
  } = useURLSyncedFilters<Omit<ParserEventLogQueryFilters, 'limit' | 'offset'>>({
    defaults: {
      parserName: undefined,
      status: undefined,
      since: undefined,
      until: undefined,
    },
    paramKeyMap: {
      parser_name: 'parserName',
    },
  });

  const limit = filters.limit;
  const offset = filters.offset;

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await limsClient.getParserEvents({ ...filters, labId: eventLabId });
      setEvents(response.data);
      setCount(response.pagination.count);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load parser events';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [searchParams, eventLabId]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    if (expandedId == null) return;
    if (liveTestStates[expandedId]) return;
    const event = events.find(e => e.id === expandedId);
    const testIds: number[] = event?.result?.matchedTests?.map((t: any) => t.testId) ?? [];
    if (testIds.length === 0) return;

    let cancelled = false;
    setTestStatesLoading(expandedId);
    limsClient.getTests({
      filters: [{ attribute_name: 'id', operator: 'IN', value: testIds as any }],
      limit: testIds.length,
    }).then((resp) => {
      if (cancelled) return;
      const stateMap: Record<number, string> = {};
      for (const test of resp.data) {
        if (test.id != null) {
          stateMap[test.id] = (test.attributes?.state as string) ?? 'unknown';
        }
      }
      setLiveTestStates(prev => ({ ...prev, [expandedId]: stateMap }));
    }).catch(() => {}).finally(() => {
      if (!cancelled) setTestStatesLoading(null);
    });
    return () => { cancelled = true; };
  }, [expandedId]);

  const handleViewFile = useCallback(async (eventId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setFileLoading(eventId);
    try {
      const file = await limsClient.getParserEventFile(eventId);
      setFileViewer(file);
    } catch (err) {
      console.error('Failed to load file:', err);
    } finally {
      setFileLoading(null);
    }
  }, []);

  const handleRerun = useCallback(async (eventId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setRerunLoading(eventId);
    try {
      await limsClient.rerunParserEvent(eventId);
      await loadEvents();
    } catch (err) {
      console.error('Failed to rerun parser event:', err);
    } finally {
      setRerunLoading(null);
    }
  }, [loadEvents]);

  const getResultSummary = (result: Record<string, any>) => {
    const matched = result?.matchedTests?.length ?? 0;
    const updated = result?.updatedTests?.length ?? result?.updated ?? 0;
    const errors = result?.errors?.length ?? 0;
    return { matched, updated, errors };
  };

  return (
    <div className="parser-logs-view">
      <div className="parser-logs-view-header">
        <Breadcrumb items={[{ label: 'Mariana Labs', path: '/dashboard' }, { label: 'Activity' }, { label: 'Parser Logs' }]} />
        <span className="parser-logs-header-info">
          {count > 0 ? `${offset + 1}–${Math.min(offset + limit, count)} of ${count}` : 'No events'}
        </span>
        <div className="parser-logs-header-controls">
          <label className="parser-logs-limit-label">
            Per page:
            <select className="limit-select" value={limit} onChange={(e) => handleLimitChange(parseInt(e.target.value, 10))}>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </label>
        </div>
        {error && <div className="error-message">Error: {error}</div>}
      </div>

      <div className="parser-logs-filters">
        <div className="parser-logs-filter-group">
          <label>Parser:</label>
          <select
            className="parser-logs-filter-select"
            value={filters.parserName || ''}
            onChange={(e) => handleFilterChange('parserName', e.target.value)}
          >
            <option value="">All parsers</option>
            {parsers.map((p) => (
              <option key={p.name} value={p.name}>{p.name}</option>
            ))}
          </select>
        </div>

        <div className="parser-logs-filter-group">
          <label>Status:</label>
          <select
            className="parser-logs-filter-select"
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="parser-logs-filter-group">
          <label>Since:</label>
          <input
            type="datetime-local"
            className="parser-logs-filter-input"
            value={filters.since || ''}
            onChange={(e) => handleFilterChange('since', e.target.value)}
          />
        </div>

        <div className="parser-logs-filter-group">
          <label>Until:</label>
          <input
            type="datetime-local"
            className="parser-logs-filter-input"
            value={filters.until || ''}
            onChange={(e) => handleFilterChange('until', e.target.value)}
          />
        </div>

        {hasActiveFilters && (
          <button className="parser-logs-filter-clear" onClick={handleClearFilters}>
            Clear filters
          </button>
        )}
      </div>

      <div className="parser-logs-table-container">
        {loading ? (
          <div className="parser-logs-loading">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading...</div>
          </div>
        ) : events.length === 0 ? (
          <div className="parser-logs-empty-state">
            <span className="empty-state-icon"><ParserLogIcon /></span>
            <p>No parser events recorded yet</p>
          </div>
        ) : (
          <div className="parser-logs-table-wrapper">
            <table className="parser-logs-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Timestamp</th>
                  <th>Parser</th>
                  <th>File Name</th>
                  <th>Status</th>
                  <th>Duration (ms)</th>
                  <th>Created By</th>
                  <th>Matched / Updated / Errors</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => {
                  const summary = getResultSummary(event.result);
                  const isExpanded = expandedId === event.id;
                  return (
                    <React.Fragment key={event.id}>
                      <tr
                        className={`parser-logs-row ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => setExpandedId(isExpanded ? null : event.id)}
                      >
                        <td>{event.id}</td>
                        <td>{formatDate(event.createdAt)}</td>
                        <td>{event.parserName}</td>
                        <td className="parser-logs-filename">
                          {event.fileName}
                          {event.result?.rerunOfEventId != null && (
                            <span className="rerun-tag">rerun of #{event.result.rerunOfEventId}</span>
                          )}
                        </td>
                        <td>
                          <span className={`parser-status-badge ${event.status}`}>
                            {event.status}
                          </span>
                        </td>
                        <td>{event.durationMs ?? '-'}</td>
                        <td>{event.createdBy || '-'}</td>
                        <td>
                          <span className="parser-logs-summary">
                            <span className="summary-matched">{summary.matched}</span>
                            {' / '}
                            <span className="summary-updated">{summary.updated}</span>
                            {' / '}
                            <span className="summary-errors">{summary.errors}</span>
                          </span>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="parser-logs-detail-row">
                          <td colSpan={8}>
                            <div className="parser-logs-detail">
                              <div className="detail-section detail-actions">
                                <button
                                  className="detail-view-file-btn"
                                  onClick={(e) => handleViewFile(event.id, e)}
                                  disabled={fileLoading === event.id}
                                >
                                  {fileLoading === event.id ? 'Loading...' : 'View File'}
                                </button>
                                {auth?.isAdmin && (
                                  <button
                                    className="detail-rerun-btn"
                                    onClick={(e) => handleRerun(event.id, e)}
                                    disabled={rerunLoading === event.id}
                                  >
                                    {rerunLoading === event.id ? 'Running...' : 'Rerun'}
                                  </button>
                                )}
                              </div>
                              {event.result?.matchedTests?.length > 0 && (() => {
                                const stateMap = liveTestStates[event.id];
                                const statesLoading = testStatesLoading === event.id;
                                return (
                                  <div className="detail-section">
                                    <h4>Matched Tests ({event.result.matchedTests.length})</h4>
                                    <table className="matched-tests-table">
                                      <thead>
                                        <tr>
                                          <th>Sample</th>
                                          <th>Test</th>
                                          <th>Assay</th>
                                          <th>Current State</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {event.result.matchedTests.map((t: any, i: number) => {
                                          const state = stateMap?.[t.testId];
                                          return (
                                            <tr key={i}>
                                              <td>
                                                <Link to={`/sample/${t.sampleId}`} className="matched-test-link" onClick={(e) => e.stopPropagation()}>
                                                  {t.sampleId}
                                                </Link>
                                              </td>
                                              <td>
                                                <Link to={`/test/${t.testId}`} className="matched-test-link" onClick={(e) => e.stopPropagation()}>
                                                  {t.testId}
                                                </Link>
                                              </td>
                                              <td className="matched-test-assay">
                                                {t.assayTitle || `#${t.assayId}`}
                                              </td>
                                              <td>
                                                {statesLoading ? (
                                                  <span className="matched-test-state loading">...</span>
                                                ) : state ? (
                                                  <span className={`matched-test-state ${state === 'COMPLETED' ? 'completed' : ''}`}>
                                                    {state}
                                                  </span>
                                                ) : (
                                                  <span className="matched-test-state">-</span>
                                                )}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                );
                              })()}
                              {event.result?.skippedSamples?.length > 0 && (
                                <div className="detail-section">
                                  <h4>Skipped Samples ({event.result.skippedSamples.length})</h4>
                                  <div className="detail-list">
                                    {event.result.skippedSamples.map((s: any, i: number) => (
                                      <span key={i} className="detail-tag warning">
                                        {typeof s === 'object' ? `${s.sampleId}: ${s.reason}` : s}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {event.result?.warnings?.length > 0 && (
                                <div className="detail-section">
                                  <h4>Warnings ({event.result.warnings.length})</h4>
                                  <div className="detail-list">
                                    {event.result.warnings.map((w: string, i: number) => (
                                      <span key={i} className="detail-tag warning">{w}</span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {event.result?.errors?.length > 0 && (
                                <div className="detail-section">
                                  <h4>Errors ({event.result.errors.length})</h4>
                                  <div className="detail-list">
                                    {event.result.errors.map((e: string, i: number) => (
                                      <span key={i} className="detail-tag error">{e}</span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {!event.result?.matchedTests?.length &&
                                !event.result?.skippedSamples?.length &&
                                !event.result?.warnings?.length &&
                                !event.result?.errors?.length && (
                                <pre className="detail-json">{JSON.stringify(event.result, null, 2)}</pre>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && events.length > 0 && (
          <PaginationControls
            offset={offset}
            limit={limit}
            count={count}
            onPageChange={handlePageChange}
            className="parser-logs-pagination"
          />
        )}
      </div>
      {fileViewer && (
        <FileViewer
          fileName={fileViewer.fileName}
          content={fileViewer.content}
          onClose={() => setFileViewer(null)}
        />
      )}
    </div>
  );
};

export default ParserLogsView;
